/* eslint-disable react-refresh/only-export-components */
import { createContext, use, useMemo } from 'react';

import { useScreenSize } from '../../../hooks/use-screen-size';
import { useSidebar } from '../../../hooks/use-sidebar';

// Fixed element dimensions and positions (matching actual implementations)
const SIDEBAR_TRIGGER_SIZE = 40; // h-10 w-10 = 2.5rem = 40px
const SIDEBAR_TRIGGER_LEFT_OFFSET = 8; // left-2 = 0.5rem = 8px
const SYNC_STATUS_SIZE = 40; // icon button size
const SYNC_STATUS_RIGHT_OFFSET = 8; // right-2 = 0.5rem = 8px

// Padding buffer for spacing between content and fixed UI
const PADDING_BUFFER = 4;

export type FixedUIIntersectionContextValue = {
    /**
     * Whether the sidebar trigger is currently visible (mobile + sidebar closed)
     */
    isSidebarTriggerVisible: boolean;
    /**
     * Whether the sync status button is visible (always true for now)
     */
    isSyncStatusVisible: boolean;
    /**
     * Padding needed on the left to avoid the sidebar trigger
     */
    paddingLeft: number;
    /**
     * Padding needed on the right to avoid the sync status
     */
    paddingRight: number;
};

export const FixedUIIntersectionContext = createContext<FixedUIIntersectionContextValue | null>(null);

/**
 * Hook to get fixed UI intersection values.
 * Returns null if used outside of FixedUIIntersectionProvider.
 */
export function useFixedUIIntersection(): FixedUIIntersectionContextValue | null {
    return use(FixedUIIntersectionContext);
}

/**
 * Hook to get fixed UI intersection values with fallback defaults.
 * Safe to use outside of FixedUIIntersectionProvider.
 */
export function useFixedUIIntersectionWithDefaults(): FixedUIIntersectionContextValue {
    const context = use(FixedUIIntersectionContext);

    return useMemo(() => {
        if (context) {
            return context;
        }

        // Default values when no provider exists
        return {
            isSidebarTriggerVisible: false,
            isSyncStatusVisible: true,
            paddingLeft: 0,
            paddingRight: SYNC_STATUS_RIGHT_OFFSET + SYNC_STATUS_SIZE + PADDING_BUFFER,
        };
    }, [context]);
}

export type FixedUIIntersectionProviderProps = {
    children: React.ReactNode;
    /**
     * Override sidebar trigger visibility (useful for testing or custom layouts)
     */
    forceSidebarTriggerVisible?: boolean;
    /**
     * Override sync status visibility
     */
    forceSyncStatusVisible?: boolean;
};

/**
 * Provider that tracks fixed UI element visibility and provides padding values.
 * Should be placed inside SidebarProvider to detect sidebar state.
 */
export function FixedUIIntersectionProvider({
    children,
    forceSidebarTriggerVisible,
    forceSyncStatusVisible,
}: FixedUIIntersectionProviderProps) {
    const { isMobile } = useScreenSize();

    // Try to get sidebar context - may not exist if used outside SidebarProvider
    let sidebarOpenMobile = false;
    try {
        const sidebar = useSidebar();
        sidebarOpenMobile = sidebar.openMobile;
    }
    catch {
        // Not inside SidebarProvider, assume sidebar is not open
    }

    const contextValue = useMemo<FixedUIIntersectionContextValue>(() => {
        // Sidebar trigger is visible on mobile when sidebar is closed
        const isSidebarTriggerVisible = forceSidebarTriggerVisible ?? (isMobile && !sidebarOpenMobile);

        // Sync status is always visible (can be overridden)
        const isSyncStatusVisible = forceSyncStatusVisible ?? true;

        // Calculate padding values
        const paddingLeft = isSidebarTriggerVisible
            ? SIDEBAR_TRIGGER_LEFT_OFFSET + SIDEBAR_TRIGGER_SIZE + PADDING_BUFFER
            : 0;

        const paddingRight = isSyncStatusVisible
            ? SYNC_STATUS_RIGHT_OFFSET + SYNC_STATUS_SIZE + PADDING_BUFFER
            : 0;

        return {
            isSidebarTriggerVisible,
            isSyncStatusVisible,
            paddingLeft,
            paddingRight,
        };
    }, [isMobile, sidebarOpenMobile, forceSidebarTriggerVisible, forceSyncStatusVisible]);

    return (
        <FixedUIIntersectionContext value={contextValue}>
            {children}
        </FixedUIIntersectionContext>
    );
}
